import { isEmpty } from 'lodash';
import { Schema } from 'normalizr';
import { Middleware } from 'redux';

import {
  PromiseActionError,
  PromiseActionResponse,
  PromiseFlaggedPayload
} from './types';
import {
  getFulfilledAction,
  getPendingAction,
  getRejectedAction,
  getSchema,
  isPromiseRequest,
  normaliseData
} from './utils';

/**
 * Promise Middleware for processing promise actions before it is returned to the client.
 * @returns {Middleware}
 */
export const promiseMiddleware = (): Middleware => 
  /**
   * Promise Middleware.
   * @returns {any}
   */
  ({ dispatch }) => next => action => {
    if (!isPromiseRequest(action)) {
      return next(action);
    }

    /**
     * Pass the pending action to the next middleware:
     * This object describes the pending state of a promise and could include
     * any data (for optimistic updates) and/or meta from the original action.
     */
    next(getPendingAction(action));

    return action.payload
      .then((response): PromiseFlaggedPayload => {
        const schema: Schema = getSchema(action);

        if (!isEmpty(schema)) {
          const normalisedData = normaliseData(response.data, schema);
          response.data = normalisedData;
        }

        const nextAction: PromiseActionResponse = getFulfilledAction(action, response);

        /** Send the fulfilled action to the init of the middleware chain */
        dispatch(nextAction);

        return response;
      })
      .catch((error): never => {
        const nextAction: PromiseActionError = getRejectedAction(action, error);

        /** Send the rejected action to the init of the middleware chain */
        dispatch(nextAction);

        throw error;
      });
  }
;
